import {
    UserOutlined,
    BankOutlined,
    DollarCircleOutlined,
    FileTextOutlined,
    ClockCircleOutlined,
    SwapOutlined,
} from '@ant-design/icons';
import { SCOPES } from '../../../constants/scopes.constants';
import { toneFor, meaningFor } from '../../common/statusClassification/statusClassification';

/**
 * THE sync-status mapping of the admin. Every screen that shows sync status —
 * the Users table cell, the user detail panel, the user page payroll card and
 * the uploads simulator — reads labels, tones and meanings from here, so the
 * same underlying state renders identically everywhere.
 *
 * The vocabulary is the server's canonical list (clockout-service
 * `docs/sync-status.md`, enums in `integrations/constants.js`):
 *  - rollup states:    IN_PROGRESS, RETRY, PARTIAL, COMPLETE, FAILED,
 *                      DIRECT_DEPOSIT_SWITCH, plus the synthetic NONE
 *  - dimension states: success, pending, dataNotAvailable, error,
 *                      platformError, failed
 *
 * Colour semantics (each colour means one thing, and colour is never the only
 * signal — every pill and icon carries a label, a tooltip and an accessible
 * name):
 *  - success (green)  healthy / done
 *  - info    (blue)   in motion — something is actively happening, nobody
 *                     needs to act yet. Distinct from green on purpose.
 *  - warn    (amber)  needs attention; automated recovery may still happen.
 *  - error   (red)    failure
 *  - neutral (grey)   no information / does not apply
 *
 * ROLLUP tones are NOT defined here (KAN-618 for KAN-584): the service
 * classifies every rollup state as healthy / in_progress / problem and serves
 * the catalog; `describeRollup` asks `toneFor(state)`
 * (common/statusClassification), which maps the class to success / info /
 * error and renders an unclassified or not-yet-loaded state NEUTRAL with a
 * console.error — never green, never silently red. Only the label and the
 * fallback description live here. Dimension states are the detail report's
 * vocabulary, not row states; they keep their tones below.
 */
export const SYNC_TONES = Object.freeze({
    SUCCESS: 'success',
    INFO: 'info',
    WARN: 'warn',
    ERROR: 'error',
    NEUTRAL: 'neutral',
});

export const SYNC_ROLLUP_STATES = Object.freeze({
    COMPLETE: {
        label: 'Complete',
        description:
            'Every required payroll dimension is healthy, covered by data on file, or was marked complete by an admin.',
    },
    IN_PROGRESS: {
        label: 'In Progress',
        description: 'Payroll link created; provider webhooks are still arriving.',
    },
    RETRY: {
        label: 'Retrying',
        description:
            'Settled with a required dimension unhealthy, inside the 24-hour window in which inference or a late webhook may still complete it. The user has not been asked to do anything yet.',
    },
    PARTIAL: {
        label: 'Partial',
        description: 'Some required payroll dimensions never came back healthy and the retry window is over.',
    },
    FAILED: {
        label: 'Failed',
        description: 'The payroll link itself failed — the provider never authenticated or returned.',
    },
    DIRECT_DEPOSIT_SWITCH: {
        label: 'DD Switch',
        description: 'A direct-deposit-switch flow, not a payroll sync.',
    },
    NONE: {
        label: 'No Sync',
        description: 'This user has not started a payroll sync yet.',
    },
});

/**
 * The report a never-synced member reads as. The service answers 200 with
 * this shell (`rollup_status: NONE`) for a member without a payroll sync row;
 * an empty body from an older server maps to the same thing, so every screen
 * (Reports panel, user page) shows "No Sync" rather than a load failure.
 */
export const SYNC_REPORT_NONE = Object.freeze({ status: 'NONE', rollup_status: 'NONE', dimensions: {} });

/**
 * Rendered for a rollup value this build has no LABEL for — never in-progress,
 * never the raw string. Its tone still comes from the served classification:
 * a state the server classified but this build cannot name keeps its class
 * colour; one the server did not classify renders neutral (+ console.error).
 */
export const SYNC_ROLLUP_UNKNOWN = Object.freeze({
    key: 'UNKNOWN',
    label: 'Unknown',
});

/** Rendered when the status could not be LOADED (not a state). */
export const SYNC_ROLLUP_UNAVAILABLE = Object.freeze({
    key: 'UNAVAILABLE',
    label: 'Unavailable',
    tone: SYNC_TONES.NEUTRAL,
    description: 'Sync status could not be loaded. Retry to fetch it again.',
});

/**
 * @param {string | null | undefined} status rollup value from the server
 *        (`row.syncStatus.status` or `report.rollup_status`)
 * @returns {{ key: string, label: string, tone: string, description: string, recognised: boolean, raw: string|null }}
 */
export function describeRollup(status) {
    if (status == null || status === '') {
        return { ...SYNC_ROLLUP_UNAVAILABLE, recognised: false, raw: null };
    }
    const key = String(status);
    // Tone from the classification the service serves, never from the name.
    const tone = toneFor(key);
    if (Object.prototype.hasOwnProperty.call(SYNC_ROLLUP_STATES, key)) {
        const entry = SYNC_ROLLUP_STATES[key];
        return {
            key,
            recognised: true,
            raw: key,
            tone,
            label: entry.label,
            description: meaningFor(key) || entry.description,
        };
    }
    return {
        ...SYNC_ROLLUP_UNKNOWN,
        tone,
        recognised: false,
        raw: key,
        description: `The server reported a sync state this admin build does not recognise ("${key}").`,
    };
}

export const SYNC_DIMENSION_STATES = Object.freeze({
    success: {
        label: 'Success',
        tone: SYNC_TONES.SUCCESS,
        description: 'Data received and stored.',
    },
    pending: {
        label: 'In Progress',
        tone: SYNC_TONES.INFO,
        description: 'No outcome from the payroll provider yet.',
    },
    dataNotAvailable: {
        label: 'No Data Available',
        tone: SYNC_TONES.NEUTRAL,
        description:
            'The provider answered and has no data for this dimension. Not an error; another path (manual upload, alternate direct deposit) may cover it.',
    },
    error: {
        label: 'Error',
        tone: SYNC_TONES.ERROR,
        description: 'The provider returned an error for this job. Retryable in principle.',
    },
    platformError: {
        label: 'Platform Error',
        tone: SYNC_TONES.ERROR,
        description: 'The payroll platform itself failed or is in an outage for this job.',
    },
    failed: {
        label: 'Failed',
        tone: SYNC_TONES.ERROR,
        description: 'Terminal failure after our own processing; will not self-heal.',
    },
});

const DIMENSION_STATE_BY_LOWER = Object.freeze(
    Object.fromEntries(Object.keys(SYNC_DIMENSION_STATES).map((key) => [key.toLowerCase(), key])),
);

export const SYNC_DIMENSION_NOT_REPORTED = Object.freeze({
    key: 'not_reported',
    label: 'Not Reported',
    tone: SYNC_TONES.NEUTRAL,
    description:
        'The sync report did not include this dimension — it was not required for this user or has not been loaded.',
});

export const SYNC_DIMENSION_NOT_APPLICABLE = Object.freeze({
    key: 'not_applicable',
    label: 'N/A',
    tone: SYNC_TONES.NEUTRAL,
    description: 'Does not apply to this user (for example shifts for a salaried employee).',
});

export const SYNC_DIMENSION_UNKNOWN = Object.freeze({
    key: 'unknown',
    label: 'Unknown',
    tone: SYNC_TONES.WARN,
});

/**
 * The dimensions, in ONE order (the server's `buildSyncDimensionsReport`
 * order). `requiredScope` gates the tooltip's detail rows only, never the
 * icon or its state — masking happens client-side. `applies(report)` lets a
 * dimension opt out for a user (salaried employees have no shifts).
 */
export const SYNC_DIMENSIONS = Object.freeze([
    { key: 'identity', label: 'Identity', Icon: UserOutlined, requiredScope: SCOPES.IDENTITY_VIEW },
    { key: 'employment', label: 'Employment', Icon: BankOutlined, requiredScope: SCOPES.EMPLOYMENT_VIEW },
    { key: 'income', label: 'Income', Icon: DollarCircleOutlined, requiredScope: SCOPES.INCOME_VIEW },
    { key: 'paystubs', label: 'Paystubs', Icon: FileTextOutlined },
    {
        key: 'direct_deposit_allocations',
        label: 'Direct Deposit',
        Icon: SwapOutlined,
        requiredScope: SCOPES.DIRECT_DEPOSIT_VIEW,
    },
    { key: 'shifts', label: 'Shifts', Icon: ClockCircleOutlined, applies: (report) => !report?.salaried },
]);

export function isDimensionApplicable(key, report) {
    const dimension = SYNC_DIMENSIONS.find((d) => d.key === key);
    if (!dimension) return true;
    return typeof dimension.applies !== 'function' || dimension.applies(report);
}

/**
 * Display entry for one dimension of a report.
 *
 * @param {{ status?: string, required?: boolean } | null | undefined} entry `report.dimensions[key]`
 * @param {{ key: string, report?: object | null }} meta
 * @returns {{ key: string, label: string, tone: string, description: string, recognised: boolean, raw: string|null, required: boolean, applicable: boolean }}
 */
export function describeDimension(entry, { key, report } = {}) {
    const applicable = isDimensionApplicable(key, report);
    const required = !!entry?.required;
    if (!applicable) {
        return { ...SYNC_DIMENSION_NOT_APPLICABLE, recognised: true, raw: null, required: false, applicable: false };
    }
    if (!entry) {
        return { ...SYNC_DIMENSION_NOT_REPORTED, recognised: true, raw: null, required: false, applicable: true };
    }
    const raw = entry.status == null ? null : String(entry.status);
    const canonical = raw ? DIMENSION_STATE_BY_LOWER[raw.toLowerCase()] : null;
    if (canonical) {
        return {
            key: canonical,
            recognised: true,
            raw,
            required,
            applicable: true,
            ...SYNC_DIMENSION_STATES[canonical],
        };
    }
    return {
        ...SYNC_DIMENSION_UNKNOWN,
        recognised: false,
        raw,
        required,
        applicable: true,
        description: raw
            ? `The server reported a dimension state this admin build does not recognise ("${raw}").`
            : 'The server reported this dimension without a state.',
    };
}

/**
 * Whole-report view: the rollup (from `rollup_status`, the canonical
 * lifecycle state; `status` is the consumer's effective access status and is
 * only a fallback for servers that predate `rollup_status`), every dimension
 * in canonical order, and a consistency note when the two disagree — e.g. an
 * admin force-marked the row COMPLETE over a required dimension in error. The
 * pill can therefore never contradict the icons silently.
 *
 * @param {object | null | undefined} report
 * @param {{ rollupFallback?: string | null }} [options] rollup to use when the report has none
 */
export function describeSyncReport(report, { rollupFallback = null } = {}) {
    const rollupValue = report?.rollup_status ?? rollupFallback ?? report?.status ?? null;
    const rollup = describeRollup(rollupValue);
    const dimensions = SYNC_DIMENSIONS.map((dimension) => ({
        ...dimension,
        entry: report?.dimensions?.[dimension.key] || null,
        state: describeDimension(report?.dimensions?.[dimension.key], { key: dimension.key, report }),
    }));

    let inconsistency = null;
    const unhealthyRequired = dimensions.filter((d) => d.state.required && d.state.tone === SYNC_TONES.ERROR);
    if (rollup.key === 'COMPLETE' && unhealthyRequired.length) {
        inconsistency = `Marked complete while ${unhealthyRequired.length} required dimension${unhealthyRequired.length > 1 ? 's report' : ' reports'} an error (${unhealthyRequired.map((d) => d.label).join(', ')}) — an admin override or data covered from another source.`;
    } else if (rollup.key === 'NONE' && dimensions.some((d) => d.entry)) {
        inconsistency = 'No sync row, yet dimension data is present — the report and the rollup disagree.';
    }

    return { rollup, dimensions, inconsistency };
}

/**
 * Derives the "Work Type" label from a sync report's `salaried` flag.
 * @param {object | null | undefined} report
 * @returns {string}
 */
export function getWorkTypeLabel(report) {
    if (!report) return '—';
    if (report.salaried === true) return 'Salary';
    if (report.salaried === false) return 'Hourly';
    return '—';
}
